/* The mock universe: a coherent, AI21-flavoured snapshot of an engineering
 * org's Claude-Code traffic through the gateway. Static catalogs + base rates
 * only — all the math that turns these into query-shaped responses lives in
 * derive.ts. Tuning the dashboard's feel (which tool dominates, how much the
 * pipeline saves) happens here. */

import type { AgentKind, OrgStructure } from "../types";
import { DASHBOARD_NOW, DATA_THROUGH } from "../clock";

/** Fixed mock clock. The dashboard is "live" against this instant so the data
 *  always lines up with today's date without depending on the wall clock. */
export const MOCK_NOW = DASHBOARD_NOW;
/** BigQuery freshness lag (minutes→hours). Drives the "data through" note. */
export const MOCK_DATA_THROUGH = DATA_THROUGH;
/** Price table used for re-costing at query time. */
export const PRICING_VERSION = "2026-06-01";
/** Per-tool / Bash / prefix breakdowns only exist after the enrichment deploy. */
export const CONTEXT_BREAKDOWN_SINCE = "2026-05-12";

export const ORG_NAME = "AI21";

interface RepoDef {
  name: string;
  /** Relative traffic weight within the org. */
  weight: number;
  /** Bias knobs (0–1-ish) that tilt this repo's tool mix, so scopes differ. */
  bias?: { read?: number; bash?: number; edit?: number };
}

interface TeamDef {
  id: string;
  name: string;
  repos: RepoDef[];
}

/** Teams → repos. Team attribution is deferred in the v1 contract, so a team
 *  scope is translated into the `repo` filter set of its repos (see derive). */
export const TEAMS: TeamDef[] = [
  {
    id: "platform",
    name: "Platform",
    repos: [
      { name: "coding-gateway", weight: 30, bias: { bash: 0.6, edit: 0.5 } },
      { name: "model-router", weight: 16, bias: { edit: 0.6 } },
      { name: "telemetry-pipeline", weight: 13, bias: { bash: 0.7 } },
    ],
  },
  {
    id: "studio",
    name: "Studio",
    repos: [
      { name: "studio-web", weight: 24, bias: { edit: 0.8, read: 0.6 } },
      { name: "studio-api", weight: 18, bias: { bash: 0.5 } },
      { name: "jamba-playground", weight: 11, bias: { edit: 0.7 } },
    ],
  },
  {
    id: "research",
    name: "Research",
    repos: [
      { name: "eval-harness", weight: 15, bias: { bash: 0.8, read: 0.7 } },
      { name: "maestro", weight: 12, bias: { read: 0.6 } },
      { name: "datasets-tooling", weight: 9, bias: { bash: 0.9 } },
    ],
  },
  {
    id: "infra",
    name: "Infra",
    repos: [
      { name: "k8s-platform", weight: 10, bias: { bash: 0.9 } },
      { name: "terraform-modules", weight: 7, bias: { bash: 0.8 } },
      { name: "ci-orchestrator", weight: 6, bias: { bash: 0.7 } },
    ],
  },
];

export const ALL_REPOS: RepoDef[] = TEAMS.flatMap((t) => t.repos);

export const ORG_STRUCTURE: OrgStructure = {
  org: ORG_NAME,
  teams: TEAMS.map((t) => ({
    id: t.id,
    name: t.name,
    repos: t.repos.map((r) => r.name),
  })),
};

export function repoWeight(name: string): number {
  return ALL_REPOS.find((r) => r.name === name)?.weight ?? 1;
}

export function reposForTeam(teamId: string): string[] {
  return TEAMS.find((t) => t.id === teamId)?.repos.map((r) => r.name) ?? [];
}

// ---- models -----------------------------------------------------------------
export interface ModelDef {
  name: string;
  /** Base share of calls when unfiltered. */
  callShare: number;
  /** Blended $ per call (drives cost from volume + model mix). */
  costPerCall: number;
  /** A cheap routing target receives re-routed calls. */
  routeTarget?: boolean;
}

export const MODELS: ModelDef[] = [
  { name: "claude-opus-4-8", callShare: 0.72, costPerCall: 0.0452 },
  { name: "claude-sonnet-4-6", callShare: 0.14, costPerCall: 0.0181 },
  {
    name: "claude-haiku-4-5",
    callShare: 0.14,
    costPerCall: 0.0129,
    routeTarget: true,
  },
];

// ---- agents (user-defined traffic slices, §9) -------------------------------
/** A registered agent. `config_id` is the agent's bound default config and
 *  references a seeded MockConfigClient profile (so the two layers tie out).
 *  `weight` is the agent's share of all agent-attributed traffic. */
export interface AgentDef {
  agent_id: string;
  name: string;
  kind: AgentKind;
  description: string | null;
  config_id: string | null;
  weight: number;
}

/* 1:1 in the UI: an agent's config is its own profile, named after it
 * (config_id === agent_id). `cc-research` starts with no config (gateway
 * defaults). The `preset` seeds that owned profile in MockConfigClient. */
export const AGENTS: AgentDef[] = [
  {
    agent_id: "cc-platform",
    name: "Claude Code · Platform",
    kind: "claude_code",
    description: "Platform team's Claude Code agents across the gateway repos.",
    config_id: "cc-platform",
    weight: 34,
  },
  {
    agent_id: "cc-studio",
    name: "Claude Code · Studio",
    kind: "claude_code",
    description: "Studio engineers editing the web app and APIs.",
    config_id: "cc-studio",
    weight: 22,
  },
  {
    agent_id: "codex-studio-web",
    name: "Codex · Studio web",
    kind: "codex",
    description: "Codex running against the studio-web front end.",
    config_id: "codex-studio-web",
    weight: 17,
  },
  {
    agent_id: "langchain-review-bot",
    name: "LangChain review bot",
    kind: "langchain",
    description: "Automated code-review bot built on LangChain.",
    config_id: "langchain-review-bot",
    weight: 13,
  },
  {
    agent_id: "cc-research",
    name: "Claude Code · Research",
    kind: "claude_code",
    description: "Research team's eval and dataset tooling.",
    config_id: null,
    weight: 9,
  },
];

/** Preset to seed each agent-owned config with (MockConfigClient). */
export const AGENT_CONFIG_PRESET: Record<string, string> = {
  "cc-platform": "everything",
  "cc-studio": "tool-results",
  "codex-studio-web": "lossless",
  "langchain-review-bot": "baseline",
};

/** Agent ids seen in traffic but not registered (name null in the rollup) — the
 *  "claim this agent" case on the home grid. */
export const UNREGISTERED_AGENTS: Array<{
  agent_id: string;
  kind: AgentKind;
  weight: number;
}> = [{ agent_id: "codex-sandbox", kind: "other", weight: 5 }];

export const TOTAL_AGENT_WEIGHT =
  AGENTS.reduce((a, x) => a + x.weight, 0) +
  UNREGISTERED_AGENTS.reduce((a, x) => a + x.weight, 0);

// ---- configs & experiments --------------------------------------------------
export const CONFIG_IDS = [
  "everything-on",
  "routing-tooldef-lossless",
  "rtk-aggressive",
  "cache-tuned",
  "baseline-passthrough",
];

export const EXPERIMENTS = [
  "regr9-dc-everything-on-20260603-1847",
  "regr9-routing-lossless-20260602-1130",
  "regr9-baseline-20260601-0900",
  "ctx-enrich-canary-20260528-1500",
];

// ---- cost decomposition -----------------------------------------------------
/** Base share of total cost by token class (input / cache_read / cache_write /
 *  output). Claude Code re-sends context every turn → cache dominates, fresh
 *  input is a rounding error. Mirrors the contract's worked example. */
export const COST_SHARES = {
  input: 0.0009,
  cache_read: 0.372,
  cache_write: 0.462,
  output: 0.165,
} as const;

// ---- context block types ----------------------------------------------------
/** Allocation of TOTAL cost across block types (they sum to the bill), plus
 *  the clawback rate where a transformer can shrink that block. */
export interface BlockDef {
  name: string;
  share: number;
  /** Fraction of this block's cost the pipeline avoids (0 = incompressible). */
  clawbackRate: number;
}

export const BLOCK_TYPES: BlockDef[] = [
  { name: "tool_result", share: 0.43, clawbackRate: 0.31 },
  { name: "tools", share: 0.2, clawbackRate: 0 },
  { name: "text", share: 0.13, clawbackRate: 0.04 },
  { name: "tool_use", share: 0.1, clawbackRate: 0 },
  { name: "system", share: 0.09, clawbackRate: 0 },
  { name: "thinking", share: 0.05, clawbackRate: 0.46 },
];

// ---- tools (tool_result + tool_use mass, by tool) ---------------------------
export interface ToolDef {
  name: string;
  /** Share of the per-tool breakdown's cost. */
  share: number;
  /** Fraction of this tool's cost that is tool_result (vs tool_use call). */
  resultRatio: number;
  /** Clawback rate on this tool's compressible (result) mass. */
  clawbackRate: number;
  /** Which repo bias amplifies this tool. */
  biasKey?: "read" | "bash" | "edit";
}

export const TOOLS: ToolDef[] = [
  {
    name: "Read",
    share: 0.49,
    resultRatio: 0.97,
    clawbackRate: 0.44,
    biasKey: "read",
  },
  {
    name: "Bash",
    share: 0.22,
    resultRatio: 0.74,
    clawbackRate: 0.26,
    biasKey: "bash",
  },
  { name: "Grep", share: 0.09, resultRatio: 0.93, clawbackRate: 0.33 },
  {
    name: "Edit",
    share: 0.06,
    resultRatio: 0.12,
    clawbackRate: 0.05,
    biasKey: "edit",
  },
  { name: "Task", share: 0.045, resultRatio: 0.9, clawbackRate: 0.37 },
  { name: "Glob", share: 0.035, resultRatio: 0.95, clawbackRate: 0.22 },
  { name: "WebFetch", share: 0.028, resultRatio: 0.92, clawbackRate: 0.41 },
  { name: "Write", share: 0.02, resultRatio: 0.1, clawbackRate: 0.03 },
  { name: "MultiEdit", share: 0.012, resultRatio: 0.15, clawbackRate: 0.04 },
  { name: "TodoWrite", share: 0.005, resultRatio: 0.08, clawbackRate: 0 },
  { name: "WebSearch", share: 0.003, resultRatio: 0.88, clawbackRate: 0.3 },
];

// ---- Bash slice by program (git subcommand kept) ----------------------------
export interface BashDef {
  name: string;
  share: number;
  resultRatio: number;
  clawbackRate: number;
}

export const BASH_COMMANDS: BashDef[] = [
  { name: "git diff", share: 0.19, resultRatio: 0.95, clawbackRate: 0.34 },
  { name: "cat", share: 0.16, resultRatio: 0.98, clawbackRate: 0.4 },
  { name: "rg", share: 0.13, resultRatio: 0.94, clawbackRate: 0.31 },
  { name: "git show", share: 0.11, resultRatio: 0.96, clawbackRate: 0.36 },
  { name: "ls", share: 0.09, resultRatio: 0.92, clawbackRate: 0.25 },
  { name: "npm test", share: 0.08, resultRatio: 0.97, clawbackRate: 0.22 },
  { name: "git log", share: 0.06, resultRatio: 0.95, clawbackRate: 0.33 },
  { name: "find", share: 0.05, resultRatio: 0.93, clawbackRate: 0.28 },
  { name: "git status", share: 0.045, resultRatio: 0.7, clawbackRate: 0.12 },
  { name: "pytest", share: 0.04, resultRatio: 0.97, clawbackRate: 0.2 },
  { name: "sed", share: 0.025, resultRatio: 0.6, clawbackRate: 0.1 },
  { name: "jq", share: 0.02, resultRatio: 0.85, clawbackRate: 0.18 },
];

// ---- system prompt sections -------------------------------------------------
export interface SectionDef {
  name: string;
  share: number;
}

export const SYSTEM_SECTIONS: SectionDef[] = [
  { name: "Tool usage policy", share: 0.27 },
  { name: "Code conventions & style", share: 0.19 },
  { name: "Task management", share: 0.15 },
  { name: "Identity & safety", share: 0.13 },
  { name: "Git & PR workflow", share: 0.11 },
  { name: "Environment context", share: 0.09 },
  { name: "Memory & recall", share: 0.06 },
];

// ---- tool definitions (the cached `tools` field of the prefix) --------------
/** Definition sizes are unrelated to call volume — a tool's schema costs the
 *  same whether called once or 1,000 times (it's in the cached prefix). */
export const TOOL_DEF_WEIGHTS: Record<string, number> = {
  Bash: 0.16,
  Edit: 0.14,
  Read: 0.11,
  MultiEdit: 0.1,
  Task: 0.1,
  Grep: 0.08,
  Write: 0.07,
  Glob: 0.06,
  WebFetch: 0.06,
  NotebookEdit: 0.05,
  TodoWrite: 0.04,
  WebSearch: 0.03,
};

// ---- transformers -----------------------------------------------------------
/** A transformer's effect is a byte delta (the contract reports transformers in
 *  bytes, not $). `savingRate` is bytes_out vs bytes_in; negative means it grew
 *  the payload (a genuine clawback case — see image-downsampler). */
export interface TransformerDef {
  name: string;
  /** Share of total pipeline bytes-in this transformer sees. */
  inputShare: number;
  /** Fraction of runs where it actually changes anything. */
  appliedRate: number;
  /** Mean fraction of bytes removed when applied (negative = added). */
  savingRate: number;
  avgDurationMs: number;
  detailNumeric?: (runs: number, bytesSaved: number) => Record<string, number>;
  detailCategorical?: (runs: number) => Record<string, Record<string, number>>;
}

export const TRANSFORMERS: TransformerDef[] = [
  {
    name: "read-toolkit-compressor",
    inputShare: 0.42,
    appliedRate: 0.86,
    savingRate: 0.47,
    avgDurationMs: 6.2,
    detailNumeric: (runs, saved) => ({
      n_compressed: Math.round(runs * 0.86),
      n_files_touched: Math.round(runs * 1.4),
      avg_ratio_pct: 47,
      bytes_reclaimed: saved,
    }),
  },
  {
    name: "bash-output-compressor",
    inputShare: 0.19,
    appliedRate: 0.72,
    savingRate: 0.34,
    avgDurationMs: 3.1,
    detailNumeric: (runs) => ({
      n_compressed: Math.round(runs * 0.72),
      head_tail_kept_lines: 200,
    }),
  },
  {
    name: "tool-result-truncator",
    inputShare: 0.16,
    appliedRate: 0.41,
    savingRate: 0.58,
    avgDurationMs: 1.4,
    detailNumeric: (runs) => ({
      n_truncated: Math.round(runs * 0.41),
      cap_bytes: 32768,
    }),
  },
  {
    name: "thinking-stripper",
    inputShare: 0.1,
    appliedRate: 0.63,
    savingRate: 0.51,
    avgDurationMs: 0.8,
    detailNumeric: (runs) => ({ n_blocks_dropped: Math.round(runs * 2.1) }),
  },
  {
    name: "tool-def-deduper",
    inputShare: 0.06,
    appliedRate: 0.95,
    savingRate: 0.12,
    avgDurationMs: 0.5,
    detailNumeric: (runs) => ({ n_defs: 14, n_normalized: Math.round(runs) }),
  },
  {
    name: "router",
    inputShare: 0.04,
    appliedRate: 0.057,
    savingRate: 0.0,
    avgDurationMs: 2.3,
    detailNumeric: (runs) => ({ est_cost_saved_usd: runs * 0.0 }),
    detailCategorical: (runs) => ({
      verdict: {
        keep: Math.round(runs * 0.943),
        route_haiku: Math.round(runs * 0.052),
        route_sonnet: Math.round(runs * 0.005),
      },
    }),
  },
  {
    name: "image-downsampler",
    inputShare: 0.03,
    appliedRate: 0.18,
    savingRate: -0.06, // re-encode overhead occasionally grows the payload
    avgDurationMs: 11.7,
    detailNumeric: (runs) => ({ n_images: Math.round(runs * 0.18) }),
  },
];

// ---- base daily org rates (everything, all repos) ---------------------------
export const BASE = {
  /** Calls/day across the whole org. */
  callsPerDay: 4200,
  /** Sessions/day across the whole org. */
  sessionsPerDay: 185,
  /** Context byte-turns carried per call (persistence-weighted), pre-pipeline. */
  bytesBeforePerCall: 66_300,
  /** Bytes ≈ 4× tokens. */
  bytesPerToken: 4,
  /** Overall payload reduction from the pipeline. */
  savedPct: 0.376,
} as const;
